import { get } from 'lodash';

import { detectType } from '@common/utils';

import { createBoundedLruCache } from './table.util';

import type { TFilters } from '@client/hooks/useTable';
import type { FilterValue, Key } from 'antd/es/table/interface';

// F1 fix: bounded LRU replaces unbounded Map
const filterCache = createBoundedLruCache<string, TColumnFilter<any>>(100);

type BivariantCallback<T extends (...args: any[]) => any> = {
  bivarianceHack: T;
}['bivarianceHack'];

type TFilterBy = {
  nested?: string | string[];
  resolver?: BivariantCallback<(value: any) => any>;
};

type TGetFilters = {
  text: string;
  value: Key;
};

export type TColumnFilter<T> = {
  filters: TGetFilters[];
  filterBy?: TFilterBy;
  filteredValue: FilterValue | null;
  onFilter: (value: boolean | Key, record: T) => boolean;
};

type TFilterItem = {
  text: string | number;
  value: Key;
};

export type TFilterOptions = {
  nested?: string;
  resolver?: (value: unknown, type: string) => string;
  type?: 'date' | 'boolean' | 'number' | 'string';
};

const formatText = (
  value: unknown,
  type: string,
  resolver?: (v: unknown, type: string) => string,
): string => {
  if (resolver) return resolver(value, type);

  switch (type) {
    case 'date': {
      const date = new Date(value as string);
      return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
    }
    case 'boolean':
      return String(Boolean(value)).toUpperCase();
    case 'number':
      return isFinite(Number(value)) ? String(Number(value)) : 'NaN';
    case 'string':
      return String(value).trim();
    case 'object':
      return JSON.stringify(value);
    default:
      return String(value);
  }
};

// F4 fix: break only on non-null/undefined, not on falsy (0, false, "")
const getValue = <T>(data: T, nested: TFilterBy['nested'] = ''): unknown => {
  if (Array.isArray(nested)) {
    let value: unknown;
    for (const item of nested) {
      value = getValue(data, item);
      if (value !== undefined && value !== null) break;
    }
    return value;
  }
  return get(data as unknown as object, nested as string);
};

/**
 * Get filters for a given column.
 * @param {[]} data - The data array to generate filters from.
 * @param {TFilterOptions} options - Options for generating filters.
 * @returns {TFilterItem[]} An array of filters.
 */
export const getUniversalFilters = <T>(
  data: T[],
  options: TFilterOptions,
): TFilterItem[] => {
  const { nested, resolver } = options;
  const seenText = new Set<string>();
  const filters: TFilterItem[] = [];

  for (const item of data) {
    const raw = nested ? get(item, nested) : item;
    if (raw === undefined || raw === null) continue;

    const type = detectType(raw);
    const display = formatText(raw, type, resolver);

    if (seenText.has(display)) continue;
    seenText.add(display);

    filters.push({ text: display, value: display as Key });
  }

  return filters;
};

/**
 * Generates a filter configuration for a table column.
 * @param {TFilters} filteredInfo - Contains the current filter information.
 * @param {[]} [dataSource] - The data source for the table.
 * @param {string} key - The key used for filtering the column.
 * @param {TFilterBy} resolver - A function to resolve the value of the filter.
 * @returns {{filters, filteredValue, onFilter}}
 */
export const columnFilter = <T>(
  filteredInfo: TFilters,
  dataSource: T[] = [],
  key: string,
  resolver?: TFilterBy['resolver'],
): TColumnFilter<T> => {
  // F3 fix: empty array [] normalised to null — no active filter
  const rawFilter = filteredInfo?.[key];
  const filteredValue: FilterValue | null = rawFilter?.length
    ? rawFilter
    : null;

  const resolverFlag = resolver ? 'withResolver' : 'noResolver';
  const filterValuesKey = filteredValue
    ? JSON.stringify(filteredValue)
    : 'null';

  // F2 fix: build cache key before running getUniversalFilters so hits skip
  // the expensive generation entirely. Column values hash provides content-awareness.
  const columnValues = dataSource.map((item) => get(item as object, key));
  const cacheKey = `${key}:${resolverFlag}:${filterValuesKey}:${JSON.stringify(columnValues)}`;

  const cached = filterCache.get(cacheKey);
  if (cached) return cached as TColumnFilter<T>;

  const options: TFilterOptions = {
    nested: key,
    resolver: resolver ? (v: unknown) => String(resolver(v)) : undefined,
  };

  const filters = getUniversalFilters<T>(dataSource, options).map((f) => ({
    text: String(f.text),
    value: f.value,
  }));

  const onFilter = (value: boolean | Key, record: T): boolean => {
    const raw = getValue(record, key) as unknown;
    const stringValue = String(value);

    if (Array.isArray(raw)) {
      return (raw as unknown[]).some((item) => {
        const itemType = detectType(item);
        const display = formatText(item, itemType, options.resolver);
        return display === stringValue;
      });
    }

    const type = detectType(raw);
    const display = formatText(raw, type, options.resolver);
    return display === stringValue;
  };

  const result: TColumnFilter<T> = {
    filters: filters as TGetFilters[],
    filteredValue,
    onFilter,
  };

  filterCache.set(cacheKey, result);
  return result;
};
